import { useEffect, useMemo, useRef, useState } from 'react';
import { recordedQuoteCalculation } from '../quoteRecordedCalculation';
import { calculateQuoteEngine } from '../quote.api';
import { calculationRequestKey, orderChapterResults } from '../quote-line-structure';
import { lineOrderFingerprint } from '../quoteEditorModel';
import {
  adaptQuoteEngineResponse,
  buildQuoteEngineRequest,
  emptyQuoteEngineCalculation,
} from '../quoteEngineContract';

const CALCULATION_DEBOUNCE_MS = 350;

const normalizeError = (error) => {
  const structured = error?.body?.error;
  if (structured && typeof structured === 'object') {
    return {
      code: structured.code || 'CALCULATION_ERROR',
      message: structured.message || error.message || 'Le calcul du devis a échoué.',
      path: structured.path || null,
      details: structured.details || {},
      status: error.status || null,
    };
  }
  return {
    code: error?.code || 'CALCULATION_ERROR',
    message: error?.message || 'Le calcul du devis a échoué.',
    path: null,
    details: {},
    status: error?.status || null,
  };
};

export default function useQuoteCalculation(editor) {
  const [calculated, setCalculated] = useState(() => emptyQuoteEngineCalculation(editor));
  const [status, setStatus] = useState(editor ? 'stale' : 'idle');
  const [error, setError] = useState(null);
  const requestSequence = useRef(0);

  const { request, requestError } = useMemo(() => {
    if (!editor || editor.contentSchemaVersion === 2 || editor.permissions?.canViewCosts === false || (editor.currencyCode ?? 'EUR') !== 'EUR' || (editor.versionStatus && editor.versionStatus !== 'draft')) return { request: null, requestError: null };
    try { return { request: buildQuoteEngineRequest(editor), requestError: null }; }
    catch (error) { return { request: null, requestError: normalizeError(error) }; }
  }, [editor]);
  const requestKey = useMemo(
    () => editor?.savedLineOrderFingerprint && editor.savedLineOrderFingerprint === lineOrderFingerprint(editor)
      ? calculationRequestKey(requestError || request) : JSON.stringify(requestError || request),
    [request, requestError, editor]
  );

  useEffect(() => {
    if (!editor) {
      setCalculated(emptyQuoteEngineCalculation(editor));
      setStatus('idle');
      setError(null);
      return undefined;
    }

    const sequence = ++requestSequence.current;
    if (editor.contentSchemaVersion === 2) {
      const model = editor.composition, totals = model?.scopes?.[0]?.totals;
      setCalculated({ internalLines: model?.scopes?.[0]?.lines || [], clientLines: [], options: [], optionViews: [], warnings: { messages: [] },
        totals: totals ? { ...totals, totalSellingPriceHT: totals.totalHT, totalVatAmount: totals.totalVat, totalSellingPriceTTC: totals.totalTTC } : {},
        taxBreakdown: [], composition: model });
      setStatus(model?.state === 'complete' ? 'validated' : 'stale'); setError(null);
      return () => { requestSequence.current += 1; };
    }
    if (editor.permissions?.canViewCosts === false || (editor.currencyCode ?? 'EUR') !== 'EUR' || (editor.versionStatus && editor.versionStatus !== 'draft')) {
      setCalculated(recordedQuoteCalculation(editor));
      setStatus('recorded');
      setError(null);
      return () => { requestSequence.current += 1; };
    }
    if (requestError) {
      setCalculated(emptyQuoteEngineCalculation(editor));
      setStatus('error');
      setError(requestError);
      return () => { requestSequence.current += 1; };
    }
    setStatus('stale');
    setError(null);
    setCalculated(emptyQuoteEngineCalculation(editor));

    const timer = window.setTimeout(async () => {
      setStatus('calculating');
      try {
        const response = await calculateQuoteEngine(request);
        if (sequence !== requestSequence.current) return;
        setCalculated(adaptQuoteEngineResponse(response, editor));
        setStatus('validated');
        setError(null);
      } catch (requestError) {
        if (sequence !== requestSequence.current) return;
        setCalculated(emptyQuoteEngineCalculation(editor));
        setStatus('error');
        setError(normalizeError(requestError));
      }
    }, CALCULATION_DEBOUNCE_MS);

    return () => { window.clearTimeout(timer); requestSequence.current += 1; };
  // requestKey stabilise le déclenchement sur le contenu métier du devis.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, editor?.quoteVersionId, editor?.versionStatus, editor?.recordedFinancialPresentation,
    editor?.currencyCode, editor?.permissions?.canViewCosts, editor?.revision]);

  const clientLines = calculated.clientLines || [];
  const presentation = calculated.minute ? { ...calculated, minute: { ...calculated.minute,
    chapters: orderChapterResults(calculated.minute.chapters || [], editor?.sections || []),
  } } : calculated;
  return {
    calculated: presentation,
    clientLines,
    visibleLines: calculated.visibleLines || clientLines,
    calculationStatus: status,
    calculationError: error,
    calculationRequest: request,
  };
}
